import type { AuthenticatedFetch } from '../types/network'
import { isRecord } from '../utils/json'
import {
  ConversationApiError,
  type ConversationActivity,
  type ConversationListPage,
  type ConversationMessage,
  type ConversationPage,
  type DeleteResult,
  type StoredConversation,
} from '../types/conversation'

export interface PageOptions {
  cursor?: string
  limit?: number
}

function buildQuery({ cursor, limit }: PageOptions): string {
  const params = new URLSearchParams()
  if (cursor) params.set('cursor', cursor)
  if (limit !== undefined) params.set('limit', String(limit))
  const query = params.toString()
  return query ? `?${query}` : ''
}

/**
 * Follows `api/mcpProxy.ts`'s fetch/error template: take `AuthenticatedFetch`,
 * `await response.text()`, parse, and throw a typed error carrying the
 * server's `error` code on `!response.ok`.
 */
export async function requestJson<T>(
  authenticatedFetch: AuthenticatedFetch,
  url: string,
  method: 'GET' | 'DELETE' | 'POST' = 'GET',
  body?: unknown,
): Promise<T> {
  // Bodyless calls keep the exact `{ method }` init they always had.
  const init: RequestInit =
    body === undefined
      ? { method }
      : {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
  const response = await authenticatedFetch(url, init)
  const text = await response.text()
  let payload: unknown = {}
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      // A non-JSON body (e.g. an HTML 404/502 page from a dev proxy or a
      // route that isn't deployed yet) shouldn't surface as an opaque
      // SyntaxError — give callers something they can actually act on.
      throw new ConversationApiError(
        'INTERNAL_SERVER_ERROR',
        `Server returned a non-JSON ${response.status} response for ${method} ${url} (is the route deployed?)`,
        response.status,
      )
    }
  }

  if (!response.ok) {
    const code =
      isRecord(payload) && typeof payload.error === 'string'
        ? payload.error
        : 'INTERNAL_SERVER_ERROR'
    const message =
      isRecord(payload) && typeof payload.message === 'string'
        ? payload.message
        : response.statusText
    throw new ConversationApiError(code, message, response.status)
  }

  return payload as T
}

export function listConversations(
  authenticatedFetch: AuthenticatedFetch,
  conversationsEndpoint: string,
  options: PageOptions = {},
): Promise<ConversationListPage> {
  return requestJson<ConversationListPage>(
    authenticatedFetch,
    `${conversationsEndpoint}${buildQuery(options)}`,
  )
}

export function getConversation(
  authenticatedFetch: AuthenticatedFetch,
  conversationsEndpoint: string,
  threadId: string,
  options: PageOptions = {},
): Promise<ConversationPage> {
  return requestJson<ConversationPage>(
    authenticatedFetch,
    `${conversationsEndpoint}/${encodeURIComponent(threadId)}${buildQuery(options)}`,
  )
}

/**
 * `DELETE /threads/:threadId` — deletes one conversation. Rejects with a
 * `ConversationApiError` on `THREAD_NOT_FOUND` (404), `THREAD_FORBIDDEN` (403,
 * thread belongs to someone else), or `RUN_ALREADY_ACTIVE` (409, a run on
 * this thread is still in progress) among others — see the server contract.
 */
export function deleteConversation(
  authenticatedFetch: AuthenticatedFetch,
  conversationsEndpoint: string,
  threadId: string,
): Promise<DeleteResult> {
  return requestJson<DeleteResult>(
    authenticatedFetch,
    `${conversationsEndpoint}/${encodeURIComponent(threadId)}`,
    'DELETE',
  )
}

/**
 * `DELETE /threads` — deletes every conversation belonging to the caller.
 * The server refuses the whole call with `RUN_ALREADY_ACTIVE` (409) if any
 * thread has a run still in progress, rather than skipping just that one.
 */
export function deleteAllConversations(
  authenticatedFetch: AuthenticatedFetch,
  conversationsEndpoint: string,
): Promise<DeleteResult> {
  return requestJson<DeleteResult>(authenticatedFetch, conversationsEndpoint, 'DELETE')
}

/**
 * Pages `getConversation` backwards (the API's only paging direction —
 * page 1 is the newest slice) until the server reports `hasMore: false`,
 * then returns every message oldest-first so it can seed a rebuilt AG-UI
 * session losslessly.
 *
 * Activities (widgets, turn metadata) are collected from every page too. The
 * server puts each one on exactly one page — the page holding its anchor
 * message — so concatenating them never duplicates one.
 *
 * On `400 PAGINATION_INVALID` mid-paging (a cursor invalidated by
 * retention), the client rule is to discard the cursor and restart from
 * page 1 rather than surface the error — but only once per call, so a
 * second consecutive failure (a real error, not a stale cursor) still
 * propagates instead of looping forever.
 */
export async function fetchWholeConversation(
  authenticatedFetch: AuthenticatedFetch,
  conversationsEndpoint: string,
  threadId: string,
  limit = 100,
): Promise<StoredConversation> {
  const pages: ConversationMessage[][] = []
  const activityPages: ConversationActivity[][] = []
  let cursor: string | undefined
  let hasRestarted = false

  for (;;) {
    let page: ConversationPage
    try {
      page = await getConversation(
        authenticatedFetch,
        conversationsEndpoint,
        threadId,
        { cursor, limit },
      )
    } catch (error) {
      const isStaleCursor =
        error instanceof ConversationApiError &&
        error.code === 'PAGINATION_INVALID' &&
        cursor !== undefined &&
        !hasRestarted

      if (!isStaleCursor) throw error

      pages.length = 0
      activityPages.length = 0
      cursor = undefined
      hasRestarted = true
      continue
    }

    pages.unshift(page.messages)
    activityPages.unshift(page.activities ?? [])

    if (!page.page.hasMore || !page.page.nextCursor) break
    cursor = page.page.nextCursor
  }

  return { messages: pages.flat(), activities: activityPages.flat() }
}
